import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { studentsAPI, groupsAPI } from '../services/api';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getLegacyApiUrl } from '../utils/apiUtils';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

const EditStudent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groups, setGroups] = useState([]);
  const [degreeDetails, setDegreeDetails] = useState([]);
  
  // Mobile responsiveness state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024);

  // Helper function for responsive input/select styles
  const getInputStyles = () => ({
    width: '100%',
    height: isMobile ? 54 : 49,
    padding: isMobile ? '0 16px' : '0 15px',
    borderRadius: 8,
    border: '1px solid #77838F',
    fontSize: 16,
    fontFamily: 'Outfit',
    minHeight: '48px',
    WebkitAppearance: 'none',
    boxSizing: 'border-box'
  });

  const getLabelStyles = () => ({
    display: 'block',
    marginBottom: '8px',
    color: 'black',
    fontSize: isMobile ? 18 : 20,
    fontFamily: 'Outfit',
    fontWeight: '500'
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    student_id: '',
    contact_no: '',
    dob: '',
    gender: '',
    degree_programme: '',
    degree_details_id: '',
    group_id: '',
    current_year: '',
    current_semester: '',
    status: 'Active',
    password: '',
    address: '',
    enrollment_date: ''
  });

  // Store the degree_details_id for module filtering
  const [studentDegreeId, setStudentDegreeId] = useState(null);
  
  // Track degree programme changes for warning message
  const [originalDegreeId, setOriginalDegreeId] = useState(null);
  const [showDegreeChangeWarning, setShowDegreeChangeWarning] = useState(false);

  // Module enrollments state - matching AddStudentForm.jsx structure
  const [moduleEnrollments, setModuleEnrollments] = useState([
    {
      id: 1,
      year: '',
      semester: '',
      moduleId: '',
      moduleName: '',
      moduleSearchQuery: '',
      type: 'regular',
      status: 'Active',
      isExisting: false
    }
  ]);

  const [academicYear, setAcademicYear] = useState('');

  const [modulesList, setModulesList] = useState([]);
  const [filteredModules, setFilteredModules] = useState({});
  const [showModuleDropdowns, setShowModuleDropdowns] = useState({});

  // Track IDs of existing enrollments user removed
  const [deletedEnrollmentIds, setDeletedEnrollmentIds] = useState([]);

  // Fetch student data and groups
  useEffect(() => {
    fetchStudentData();
    fetchGroups();
    fetchModules();
    fetchDegreeDetails();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle responsive breakpoints
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchStudentData = async () => {
    try {
      setInitialLoading(true);
      setError(null);
      
      if (!id) {
        setError('No student ID provided in URL');
        setInitialLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        navigate('/login');
        setInitialLoading(false);
        return;
      }
      
      console.log('Fetching student by id:', id);
      const response = await studentsAPI.getStudentById(id);
      console.log('Raw response from studentsAPI.getStudentById:', response);

      // Flexible extractor to support multiple backend shapes:
      // - res.json(student)
      // - res.json({ data: student })
      // - res.json({ message: ..., data: student })
      // - res.json({ student: student })
      const extractStudent = (resp) => {
        if (!resp) return null;
        // axios-style wrapper: resp.data may be the payload
        const payload = resp.data !== undefined ? resp.data : resp;
        if (!payload) return null;

        // Common possibilities
        if (payload.student && typeof payload.student === 'object') return payload.student;
        if (payload.data && typeof payload.data === 'object' && payload.data.id) return payload.data;
        if (payload.data && Array.isArray(payload.data) && payload.data.length > 0 && payload.data[0].id) return payload.data[0];
        if (payload.id) return payload; // payload itself is student
        // fallback: if top-level has fields typical of student
        if (payload.name || payload.student_id || payload.email) return payload;
        return null;
      };

      const student = extractStudent(response);
      console.log('Extracted student object:', student);

      if (!student) {
        setError('Student data not received');
        setInitialLoading(false);
        return;
      }
      
      const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return '';
          return date.toISOString().split('T')[0];
        } catch (error) {
          console.error('Date formatting error:', error);
          return '';
        }
      };

      setFormData({
        name: student.name || '',
        email: student.email || '',
        student_id: student.student_id || '',
        contact_no: student.contact_no || student.phone || '',
        dob: formatDate(student.dob || student.date_of_birth),
        gender: student.gender || '',
        degree_programme: student.degree_programme || student.program || '',
        degree_details_id: student.degree_details_id || '',
        group_id: student.group_id || student.group || '',
        current_year: student.current_year?.toString() || student.year?.toString() || '',
        current_semester: student.current_semester?.toString() || student.semester?.toString() || '',
        status: student.active === false ? 'Inactive' : 'Active',
        password: '', // Don't pre-fill password
        address: student.address || '',
        enrollment_date: formatDate(student.enrollment_date || student.created_at)
      });

      // Store degree_details_id for module filtering
      if (student.degree_details_id) {
        setStudentDegreeId(student.degree_details_id);
        setOriginalDegreeId(student.degree_details_id); // Store original for comparison
        console.log('✅ Student degree_details_id:', student.degree_details_id);
      }

      // Load existing module enrollments
      await loadModuleEnrollments(id); // Use the URL parameter id, not student.id
      
    } catch (error) {
      console.error('Error fetching student:', error);
      
      if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
        setTimeout(() => navigate('/login'), 2000);
      } else if (error.response?.status === 404 || error?.status === 404) {
        setError('Student not found. The student may have been deleted or you may not have permission to view this student.');
      } else if (error.response?.status === 403) {
        setError('Access denied. You do not have permission to edit this student.');
      } else {
        setError(error.response?.data?.error || error.message || 'Failed to load student data');
      }
    } finally {
      setInitialLoading(false);
    }
  };

  // Initialize filtered modules for new enrollments based on student's degree
  const initializeFilteredModulesForNewEnrollment = (enrollmentId) => {
    let filtered = modulesList;

    // Filter by student's degree programme
    if (studentDegreeId) {
      filtered = filtered.filter(module => 
        module.module_degree_mappings && 
        module.module_degree_mappings.some(mapping => 
          mapping.degree_details_id === studentDegreeId
        )
      );
    }

    setFilteredModules(prev => ({ ...prev, [enrollmentId]: filtered }));
  };

  const fetchModules = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Get university_id from auth context or local storage
      let universityId = currentUser?.university_id;
      
      if (!universityId && currentUser?.id) {
        // Try to get university_id from admins table
        const { data: adminData } = await supabase
          .from('admins')
          .select('university_id')
          .eq('user_id', currentUser.id)
          .single();
        
        if (adminData) universityId = adminData.university_id;
      }

      if (!universityId) {
        console.warn('No university_id found for user, loading all modules');
      }

      // Get modules filtered by university with degree mappings (like AddStudentForm.jsx)
      let query = supabase
        .from('modules')
        .select(`
          id, 
          module_name, 
          module_code, 
          semester, 
          active,
          university_id,
          module_degree_mappings!inner(
            year,
            degree_details_id,
            is_mandatory
          )
        `)
        .eq('active', true)
        .order('module_name', { ascending: true });

      if (universityId) {
        query = query.eq('university_id', universityId);
      }

      const { data: modulesData, error } = await query;

      if (error) {
        console.error('Error fetching modules:', error);
        return;
      }

      console.log(`✅ Fetched ${modulesData?.length || 0} modules with degree mappings for university: ${universityId}`);
      setModulesList(modulesData || []);
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const loadModuleEnrollments = async (studentId) => {
    try {
      const { data: enrollmentData, error } = await supabase
        .from('student_module_enrollments')
        .select(`
          id,
          academic_year,
          semester,
          enrollment_type,
          enrolled_date,
          active,
          modules (
            id,
            module_name,
            module_code
          )
        `)
        .eq('student_id', studentId);

      if (error) {
        console.error('Error loading enrollments:', error);
        return;
      }

      console.log(`✅ Loaded ${enrollmentData?.length || 0} module enrollments for student: ${studentId}`);

      if (enrollmentData && enrollmentData.length > 0) {
        // Set academic year from first enrollment
        if (enrollmentData[0]?.academic_year) {
          setAcademicYear(enrollmentData[0].academic_year);
        }

        const mappedEnrollments = enrollmentData.map((enrollment, index) => ({
          id: enrollment.id || `existing_${index + 1}`,
          year: '', // Will be derived from student's current_year or calculated
          semester: enrollment.semester?.toString() || '',
          moduleId: enrollment.modules?.id || '',
          moduleName: enrollment.modules?.module_name || '',
          moduleSearchQuery: enrollment.modules ?
            `${enrollment.modules.module_name} (${enrollment.modules.module_code})` :
            '',
          type: enrollment.enrollment_type || 'regular',
          status: enrollment.active ? 'Active' : 'Inactive',
          isExisting: true
        }));

        setModuleEnrollments(mappedEnrollments);

        const initialFiltered = {};
        mappedEnrollments.forEach(enrollment => {
          initialFiltered[enrollment.id] = modulesList;
        });
        setFilteredModules(initialFiltered);
      } else {
        // Initialize with empty enrollment if none exist
        setFilteredModules(prev => ({ ...prev, [moduleEnrollments[0].id]: modulesList }));
      }
    } catch (error) {
      console.error('Error loading module enrollments:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await groupsAPI.getAllGroups();
      setGroups(response.data || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchDegreeDetails = async () => {
    try {
      console.log('🎓 Starting fetchDegreeDetails...');
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('❌ No authentication token found');
        return;
      }

      // Use the API to fetch degree details
      const apiUrl = getLegacyApiUrl();
      const fullUrl = `${apiUrl}/degrees`;
      
      console.log('🎓 API Configuration:');
      console.log('  - Base URL:', apiUrl);
      console.log('  - Full URL:', fullUrl);
      console.log('  - Token present:', !!token);
      console.log('  - Token preview:', token ? `${token.substring(0, 20)}...` : 'None');
      
      console.log('🎓 Making fetch request...');
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🎓 Response received:');
      console.log('  - Status:', response.status);
      console.log('  - Status Text:', response.statusText);
      console.log('  - OK:', response.ok);
      console.log('  - Headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
        } catch (e) {
          errorText = 'Could not read error response';
        }
        console.error('❌ Failed to fetch degree details:');
        console.error('  - Status:', response.status);
        console.error('  - Status Text:', response.statusText);
        console.error('  - Error Text:', errorText);
        return;
      }

      const data = await response.json();
      console.log('✅ Degree details fetch successful:');
      console.log('  - Data type:', typeof data);
      console.log('  - Is Array:', Array.isArray(data));
      console.log('  - Length:', data?.length || 'N/A');
      console.log('  - Raw data:', data);
      
      if (data && data.length > 0) {
        console.log('🎓 Sample degree:', data[0]);
      }
      
      setDegreeDetails(data || []);
      console.log('✅ DegreeDetails state updated with', (data || []).length, 'items');
    } catch (error) {
      console.error('❌ Error in fetchDegreeDetails:');
      console.error('  - Error type:', error.constructor.name);
      console.error('  - Error message:', error.message);
      console.error('  - Full error:', error);
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('💡 This looks like a network/CORS error. Check if backend is running and CORS is configured.');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle degree programme change
  const handleDegreeChange = (e) => {
    const selectedDegreeId = e.target.value;
    const selectedDegree = degreeDetails.find(d => d.id === selectedDegreeId);
    
    // Check if degree is being changed from original
    if (originalDegreeId && selectedDegreeId !== originalDegreeId) {
      setShowDegreeChangeWarning(true);
    } else {
      setShowDegreeChangeWarning(false);
    }
    
    if (selectedDegree) {
      setFormData(prev => ({
        ...prev,
        degree_details_id: selectedDegreeId,
        degree_programme: `${selectedDegree.degree_programme} (${selectedDegree.local_international})`
      }));
      setStudentDegreeId(selectedDegreeId);
    } else {
      setFormData(prev => ({
        ...prev,
        degree_details_id: '',
        degree_programme: ''
      }));
      setStudentDegreeId(null);
      setShowDegreeChangeWarning(false);
    }
  };

  // Update filtered modules based on year and semester selection (like AddStudentForm.jsx)
  const updateFilteredModulesForEnrollment = (enrollmentId, enrollment) => {
    let filtered = modulesList;

    // Filter by student's degree programme
    if (studentDegreeId) {
      filtered = filtered.filter(module => 
        module.module_degree_mappings && 
        module.module_degree_mappings.some(mapping => 
          mapping.degree_details_id === studentDegreeId
        )
      );
    }

    // Filter by year if selected
    if (enrollment.year) {
      filtered = filtered.filter(module => 
        module.module_degree_mappings && 
        module.module_degree_mappings.some(mapping => 
          mapping.year === parseInt(enrollment.year) &&
          (!studentDegreeId || mapping.degree_details_id === studentDegreeId)
        )
      );
    }

    // Filter by semester if selected
    if (enrollment.semester) {
      filtered = filtered.filter(module => 
        module.semester === parseInt(enrollment.semester)
      );
    }

    setFilteredModules(prev => ({ ...prev, [enrollmentId]: filtered }));
  };

  // Module enrollment handlers
  const handleModuleEnrollmentChange = (id, field, value) => {
    setModuleEnrollments(prev => {
      const updated = prev.map(enrollment => 
        enrollment.id === id ? { ...enrollment, [field]: value } : enrollment
      );
      
      // Update filtered modules when year or semester changes
      if (field === 'year' || field === 'semester') {
        const enrollment = updated.find(e => e.id === id);
        if (enrollment) {
          updateFilteredModulesForEnrollment(id, enrollment);
        }
      }
      
      return updated;
    });
  };

  const handleModuleSearch = (enrollmentId, query) => {
    // Ignore searches for existing DB enrollments (read-only)
    const enrollment = moduleEnrollments.find(e => e.id === enrollmentId);
    if (enrollment?.isExisting) return;

    setModuleEnrollments(prev => 
      prev.map(enrollment => 
        enrollment.id === enrollmentId 
          ? { ...enrollment, moduleSearchQuery: query }
          : enrollment
      )
    );
    
    // Get base filtered modules (filtered by degree, year, and semester)
    let baseFiltered = modulesList;
    
    // Filter by student's degree programme
    if (studentDegreeId) {
      baseFiltered = baseFiltered.filter(module => 
        module.module_degree_mappings && 
        module.module_degree_mappings.some(mapping => 
          mapping.degree_details_id === studentDegreeId
        )
      );
    }

    // Filter by year if selected
    if (enrollment.year) {
      baseFiltered = baseFiltered.filter(module => 
        module.module_degree_mappings && 
        module.module_degree_mappings.some(mapping => 
          mapping.year === parseInt(enrollment.year) &&
          (!studentDegreeId || mapping.degree_details_id === studentDegreeId)
        )
      );
    }

    // Filter by semester if selected
    if (enrollment.semester) {
      baseFiltered = baseFiltered.filter(module => 
        module.semester === parseInt(enrollment.semester)
      );
    }

    // Apply search query filter
    if (query.trim() === '') {
      setFilteredModules(prev => ({ ...prev, [enrollmentId]: baseFiltered }));
    } else {
      const searchFiltered = baseFiltered.filter(module => 
        module.module_name.toLowerCase().includes(query.toLowerCase()) ||
        module.module_code.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredModules(prev => ({ ...prev, [enrollmentId]: searchFiltered }));
    }
    setShowModuleDropdowns(prev => ({ ...prev, [enrollmentId]: true }));
  };

  const handleModuleSelect = (enrollmentId, module) => {
    // Ignore selects for existing enrollments
    const enrollment = moduleEnrollments.find(e => e.id === enrollmentId);
    if (enrollment?.isExisting) return;

    setModuleEnrollments(prev => 
      prev.map(enrollment => 
        enrollment.id === enrollmentId 
          ? { 
              ...enrollment, 
              moduleName: module.module_name,
              moduleSearchQuery: `${module.module_name} (${module.module_code})`,
              moduleId: module.id 
            }
          : enrollment
      )
    );
    setShowModuleDropdowns(prev => ({ ...prev, [enrollmentId]: false }));
  };

  const addModuleEnrollment = () => {
    const newId = `new_${Date.now()}`;
    setModuleEnrollments(prev => [
      ...prev,
      {
        id: newId,
        year: '',
        semester: '',
        moduleId: '',
        moduleName: '',
        moduleSearchQuery: '',
        type: 'regular',
        status: 'Active',
        isExisting: false
      }
    ]);
    
    // Initialize filtered modules based on student's degree programme
    initializeFilteredModulesForNewEnrollment(newId);
  };

  const removeModuleEnrollment = async (enrollmentId) => {
    const enrollment = moduleEnrollments.find(e => e.id === enrollmentId);
    if (!enrollment) return;

    console.log('[EditStudent] removeModuleEnrollment', { enrollmentId, isExisting: enrollment.isExisting });

    if (enrollment.isExisting) {
      // Try immediate delete against student_module_enrollments
      console.log('[EditStudent] delete click', { enrollmentId, studentId: id, enrollment });
      if (!window.confirm('Delete this module enrollment? This will remove the record permanently.')) return;
      
      try {
        // Validate enrollmentId looks like a DB UUID before calling backend
        const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
        if (!uuidRegex.test(enrollmentId)) {
          console.warn('[EditStudent] enrollment id is not a UUID, marking for deletion:', enrollmentId);
          setDeletedEnrollmentIds(prev => Array.from(new Set([...prev, enrollmentId])));
          setModuleEnrollments(prev => prev.filter(enrollment => enrollment.id !== enrollmentId));
          return;
        }

        setLoading(true);
        const { error } = await supabase
          .from('student_module_enrollments')
          .delete()
          .eq('id', enrollmentId)
          .eq('student_id', id); // Use the URL parameter id for student_id

        if (error) {
          console.error('[EditStudent] immediate delete failed, marking for deletion instead', error);
          setDeletedEnrollmentIds(prev => Array.from(new Set([...prev, enrollmentId])));
        } else {
          // Remove locally after successful delete
          setModuleEnrollments(prev => prev.filter(enrollment => enrollment.id !== enrollmentId));
        }
      } catch (err) {
        console.error('[EditStudent] removeModuleEnrollment exception', err);
        // Fallback: mark for deletion to attempt on submit
        setDeletedEnrollmentIds(prev => Array.from(new Set([...prev, enrollmentId])));
        // Keep item removed from UI
        setModuleEnrollments(prev => prev.filter(enrollment => enrollment.id !== enrollmentId));
      } finally {
        setLoading(false);
      }
    } else {
      // Just remove the newly added row
      setModuleEnrollments(prev => prev.filter(enrollment => enrollment.id !== enrollmentId));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!formData.name || !formData.student_id || !formData.email) {
        setError('Please fill in all required fields (Name, Student ID, Email)');
        return;
      }

      // Exclude degree_programme from updates (keep degree_details_id)
      const payload = { ...formData };
      if (payload.hasOwnProperty('degree_programme')) delete payload.degree_programme;
      // Remove emergency_contact from payload (no longer supported)
      if (payload.hasOwnProperty('emergency_contact')) delete payload.emergency_contact;
      
      // Handle status to active boolean conversion
      payload.active = formData.status === 'Active';
      delete payload.status;
      
      // Only include password if provided
      if (!formData.password || formData.password.trim() === '') {
        delete payload.password;
      }
      
      // Update student details
      await studentsAPI.updateStudent(id, payload);

      // Handle module enrollments updates
      // Delete removed enrollments
      if (deletedEnrollmentIds.length > 0) {
        console.log('[EditStudent] attempting to delete enrollment ids', deletedEnrollmentIds);
        const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
        const idsToDelete = deletedEnrollmentIds.filter(id => typeof id === 'string' && uuidRegex.test(id));
        
        if (idsToDelete.length > 0) {
          console.log('[EditStudent] idsToDelete (validated)', idsToDelete);
          try {
            const { error: delErr } = await supabase
              .from('student_module_enrollments')
              .delete()
              .in('id', idsToDelete)
              .eq('student_id', id);

            if (delErr) {
              console.error('[EditStudent] bulk delete error', delErr);
              throw delErr;
            }
          } catch (err) {
            console.error('[EditStudent] failed to delete enrollments', err);
            throw new Error(err?.message || 'Failed to delete module enrollments');
          }
        } else {
          console.warn('[EditStudent] no valid enrollment ids to delete after validation');
        }
      }

      // Insert new enrollments
      const validNew = moduleEnrollments.filter(e => !e.isExisting && e.moduleId && e.year && e.semester && e.type);
      if (validNew.length > 0) {
        const inserts = validNew.map(e => ({
          student_id: id,
          module_id: e.moduleId,
          academic_year: academicYear,
          semester: parseInt(e.semester),
          enrollment_type: e.type,
          enrolled_date: new Date().toISOString(),
          active: e.status === 'Active',
          created_at: new Date().toTimeString().split(' ')[0] + '+00:00'
        }));

        try {
          const { error: insErr } = await supabase
            .from('student_module_enrollments')
            .insert(inserts);

          if (insErr) {
            console.error('[EditStudent] insert enrollments error', insErr);
            throw insErr;
          }
        } catch (err) {
          console.error('[EditStudent] failed to insert new enrollments', err);
          throw new Error(err?.message || 'Failed to insert new enrollments');
        }
      }
      
      alert('Student and module enrollments updated successfully!');
      navigate('/students-list');
      
    } catch (error) {
      console.error('Error updating student:', error);
      setError(error.response?.data?.error || error.message || 'Failed to update student');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div style={{width: '100%', height: '100%', position: 'relative', background: 'white'}}>
        <Sidebar />
        <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh'}}>
          <Header />
          <div style={{margin: '40px', textAlign: 'center', padding: '40px'}}>
            <h2 style={{color: '#F04915', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>
              Loading Student Details...
            </h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{width: '100%', height: '100%', position: 'relative', background: 'white'}}>
      {/* Add CSS for blinking animation */}
      <style>
        {`
          @keyframes blink {
            0% { background-color: #fff3cd; border-color: #ffc107; }
            50% { background-color: #fff8dc; border-color: #ff8c00; }
            100% { background-color: #fff3cd; border-color: #ffc107; }
          }
          
          .blinking-group {
            animation: blink 1.5s infinite;
            box-shadow: 0 0 10px rgba(255, 193, 7, 0.5);
          }
        `}
      </style>
      
      <Sidebar />
      
      <div style={{
        marginLeft: isMobile ? 0 : 290, 
        width: isMobile ? '100%' : 'calc(100% - 290px)', 
        minHeight: '100vh',
        padding: isMobile ? '80px 15px 20px 15px' : '0'
      }}>
        <Header />
        
        <form onSubmit={handleSubmit}>
          {loading && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}>
              <div style={{
                background: 'white',
                padding: '20px 40px',
                borderRadius: 8,
                textAlign: 'center'
              }}>
                Updating student...
              </div>
            </div>
          )}
          
          <div style={{margin: '20px 40px', position: 'relative'}}>
            <div style={{background: '#F04915', borderTopLeftRadius: 15, borderTopRightRadius: 15, padding: '20px', color: 'white'}}>
              <h2 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>Edit Student Details</h2>
            </div>

            <div style={{background: '#FFFBF5', padding: '40px'}}>
              {error && (
                <div style={{
                  background: '#FEF2F2',
                  border: '1px solid #FECACA',
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 24,
                  color: '#DC2626',
                  fontSize: 14,
                  fontFamily: 'Outfit'
                }}>
                  {error}
                </div>
              )}

              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                {/* Left Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Full Name <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Email Address <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Student ID <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="student_id"
                      type="text"
                      value={formData.student_id}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Contact Number
                    </label>
                    <input
                      name="contact_no"
                      type="tel"
                      value={formData.contact_no}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Date of Birth
                    </label>
                    <input
                      name="dob"
                      type="date"
                      value={formData.dob}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Gender
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Right Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Degree Programme
                    </label>
                    <select
                      name="degree_details_id"
                      value={formData.degree_details_id}
                      onChange={handleDegreeChange}
                      style={{
                        width: '100%',
                        height: 49,
                        padding: '0 15px',
                        borderRadius: 8,
                        border: '1px solid #D1D5DB',
                        fontSize: 16,
                        fontFamily: 'Outfit',
                        backgroundColor: 'white',
                        color: '#374151'
                      }}
                    >
                      <option value="">Select Degree Programme</option>
                      {degreeDetails.map((degree) => (
                        <option key={degree.id} value={degree.id}>
                          {degree.degree_programme} ({degree.local_international})
                        </option>
                      ))}
                    </select>
                    
                    {/* Warning message when degree is changed */}
                    {showDegreeChangeWarning && (
                      <div style={{
                        marginTop: '8px',
                        padding: '10px 12px',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: 6,
                        color: '#856404',
                        fontSize: 14,
                        fontFamily: 'Outfit',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{fontSize: '16px'}}>⚠️</span>
                        If You change The Degree Programme then you need to change Group also
                      </div>
                    )}
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Group
                    </label>
                    <select
                      name="group_id"
                      value={formData.group_id}
                      onChange={handleInputChange}
                      className={showDegreeChangeWarning ? 'blinking-group' : ''}
                      style={{
                        width: '100%', 
                        height: 49, 
                        padding: '0 15px', 
                        borderRadius: 8, 
                        border: showDegreeChangeWarning ? '2px solid #ffc107' : '1px solid #77838F', 
                        fontSize: 16, 
                        fontFamily: 'Outfit',
                        backgroundColor: showDegreeChangeWarning ? '#fff3cd' : 'white'
                      }}
                    >
                      <option value="">Select Group</option>
                      {groups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.group_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Current Year
                    </label>
                    <select
                      name="current_year"
                      value={formData.current_year}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select Year</option>
                      <option value="1">Year 1</option>
                      <option value="2">Year 2</option>
                      <option value="3">Year 3</option>
                      <option value="4">Year 4</option>
                      <option value="5">Year 5</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Current Semester
                    </label>
                    <select
                      name="current_semester"
                      value={formData.current_semester}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select Semester</option>
                      <option value="1">Semester 1</option>
                      <option value="2">Semester 2</option>
                      <option value="3">Semester 3</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Student Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  {/* Password Change */}
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Password (Leave blank to keep current)
                    </label>
                    <input
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      autoComplete="new-password"
                      placeholder="Enter new password"
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Enrollment Date
                    </label>
                    <input
                      name="enrollment_date"
                      type="date"
                      value={formData.enrollment_date}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>
                </div>

                {/* Address - Full Width */}
                <div style={{gridColumn: '1 / -1', marginTop: '20px'}}>
                  <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                    Address
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows="3"
                    style={{
                      width: '100%', 
                      padding: '15px', 
                      borderRadius: 8, 
                      border: '1px solid #77838F', 
                      fontSize: 16, 
                      fontFamily: 'Outfit',
                      resize: 'vertical',
                      minHeight: '80px'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Module Enrollments Section */}
          <div style={{margin: '20px 40px', position: 'relative'}}>
            <div style={{background: '#F04915', borderTopLeftRadius: 15, borderTopRightRadius: 15, padding: '20px', color: 'white'}}>
              <h2 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>Module Enrollments</h2>
            </div>

            <div style={{background: '#FFFBF5', padding: '40px'}}>
              {/* Academic Year Input */}
              <div style={{marginBottom: '30px'}}>
                <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                  Academic Year <span style={{color: '#F04915'}}>*</span>
                </label>
                <select
                  value={academicYear}
                  onChange={(e) => setAcademicYear(e.target.value)}
                  style={{
                    width: '300px',
                    height: 49,
                    padding: '0 15px',
                    borderRadius: 8,
                    border: '1px solid #77838F',
                    fontSize: 16,
                    fontFamily: 'Outfit',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select Academic Year</option>
                  <option value="2023-2024">2023-2024</option>
                  <option value="2024-2025">2024-2025</option>
                  <option value="2025-2026">2025-2026</option>
                  <option value="2026-2027">2026-2027</option>
                </select>
              </div>

              {moduleEnrollments.map((enrollment, index) => (
                <div key={enrollment.id} style={{
                  marginBottom: index === moduleEnrollments.length - 1 ? '0' : '30px',
                  padding: '20px',
                  border: '1px solid #E0E0E0',
                  borderRadius: 8,
                  background: 'white'
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                    <h3 style={{fontSize: 18, fontFamily: 'Outfit', fontWeight: '600', color: '#333', margin: 0}}>
                      Module Enrollment {index + 1}
                    </h3>
                    <button
                      type="button"
                      onClick={() => removeModuleEnrollment(enrollment.id)}
                      style={{
                        background: '#DC3545',
                        border: 'none',
                        borderRadius: 6,
                        color: 'white',
                        padding: '8px 12px',
                        fontSize: 14,
                        fontFamily: 'Outfit',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  </div>

                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 2fr 1fr 1fr', gap: '20px', alignItems: 'end'}}>
                    {/* Year */}
                    <div>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 16, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Year <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <select
                        value={enrollment.year}
                        onChange={(e) => handleModuleEnrollmentChange(enrollment.id, 'year', e.target.value)}
                        disabled={enrollment.isExisting}
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit',
                          backgroundColor: enrollment.isExisting ? '#f5f5f5' : 'white',
                          cursor: enrollment.isExisting ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <option value="">Select Year</option>
                        <option value="1">Year 1</option>
                        <option value="2">Year 2</option>
                        <option value="3">Year 3</option>
                        <option value="4">Year 4</option>
                      </select>
                    </div>

                    {/* Semester */}
                    <div>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 16, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Semester <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <select
                        value={enrollment.semester}
                        onChange={(e) => handleModuleEnrollmentChange(enrollment.id, 'semester', e.target.value)}
                        disabled={enrollment.isExisting}
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit',
                          backgroundColor: enrollment.isExisting ? '#f5f5f5' : 'white',
                          cursor: enrollment.isExisting ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <option value="">Select Semester</option>
                        <option value="1">Semester 1</option>
                        <option value="2">Semester 2</option>
                      </select>
                    </div>

                    {/* Module */}
                    <div style={{position: 'relative'}}>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 16, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Module <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Search for module..."
                        value={enrollment.moduleSearchQuery}
                        onChange={(e) => handleModuleSearch(enrollment.id, e.target.value)}
                        onFocus={() => {
                          if (!enrollment.isExisting) {
                            setShowModuleDropdowns(prev => ({ ...prev, [enrollment.id]: true }));
                            if (!filteredModules[enrollment.id]) {
                              setFilteredModules(prev => ({ ...prev, [enrollment.id]: modulesList }));
                            }
                          }
                        }}
                        readOnly={enrollment.isExisting}
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: enrollment.moduleId ? '2px solid #28a745' : '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit',
                          backgroundColor: enrollment.isExisting ? '#f5f5f5' : (enrollment.moduleId ? '#f8fff8' : 'white'),
                          cursor: enrollment.isExisting ? 'default' : 'text'
                        }}
                      />
                      {enrollment.isExisting && (
                        <div style={{
                          fontSize: 12,
                          color: '#28a745',
                          marginTop: '2px',
                          fontFamily: 'Outfit'
                        }}>
                          ✓ Enrolled: {enrollment.moduleName || 'Module'}
                        </div>
                      )}
                      {enrollment.moduleId && !enrollment.isExisting && (
                        <div style={{
                          fontSize: 12,
                          color: '#28a745',
                          marginTop: '2px',
                          fontFamily: 'Outfit'
                        }}>
                          ✓ Selected: {enrollment.moduleName}
                        </div>
                      )}
                      {showModuleDropdowns[enrollment.id] && !enrollment.isExisting && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: 'white',
                          border: '1px solid #77838F',
                          borderTop: 'none',
                          borderRadius: '0 0 8px 8px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}>
                          {(filteredModules[enrollment.id] || []).length === 0 ? (
                            <div style={{
                              padding: '12px 15px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#888',
                              textAlign: 'center'
                            }}>
                              No modules found for your university
                            </div>
                          ) : (
                            (filteredModules[enrollment.id] || []).map(module => (
                              <div
                                key={module.id}
                                onClick={() => handleModuleSelect(enrollment.id, module)}
                                style={{
                                  padding: '10px 15px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0',
                                  fontSize: 14,
                                  fontFamily: 'Outfit',
                                  backgroundColor: enrollment.moduleId === module.id ? '#e3f2fd' : 'white'
                                }}
                              >
                                {module.module_name} ({module.module_code})
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    {/* Type */}
                    <div>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 16, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Type <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <select
                        value={enrollment.type}
                        onChange={(e) => handleModuleEnrollmentChange(enrollment.id, 'type', e.target.value)}
                        disabled={enrollment.isExisting}
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit',
                          backgroundColor: enrollment.isExisting ? '#f5f5f5' : 'white',
                          cursor: enrollment.isExisting ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <option value="">Select Type</option>
                        <option value="regular">Regular</option>
                        <option value="elective">Elective</option>
                        <option value="repeat">Repeat</option>
                      </select>
                    </div>

                    {/* Status */}
                    <div>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 16, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Status <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <select
                        value={enrollment.status}
                        onChange={(e) => handleModuleEnrollmentChange(enrollment.id, 'status', e.target.value)}
                        disabled={enrollment.isExisting}
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit',
                          backgroundColor: enrollment.isExisting ? '#f5f5f5' : 'white',
                          cursor: enrollment.isExisting ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Module Button */}
              <div style={{marginTop: '20px', textAlign: 'center'}}>
                <button
                  type="button"
                  onClick={addModuleEnrollment}
                  style={{
                    background: '#28A745',
                    border: 'none',
                    borderRadius: 8,
                    color: 'white',
                    padding: '12px 24px',
                    fontSize: 16,
                    fontFamily: 'Outfit',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    margin: '0 auto'
                  }}
                >
                  <span style={{fontSize: '18px'}}>+</span>
                  Add Module
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{margin: '40px', display: 'flex', justifyContent: 'space-between'}}>
            <Link to="/students-list">
              <button 
                type="button" 
                style={{
                  width: 269, 
                  padding: '11px 20px', 
                  background: '#6C757D', 
                  border: 'none', 
                  borderRadius: 8, 
                  color: 'white', 
                  fontSize: 16, 
                  fontFamily: 'Outfit', 
                  fontWeight: '500', 
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </Link>
            <button 
              type="submit" 
              disabled={loading}
              style={{
                width: 269, 
                padding: '11px 20px', 
                background: loading ? '#ccc' : '#F04915', 
                border: 'none', 
                borderRadius: 8, 
                color: 'white', 
                fontSize: 16, 
                fontFamily: 'Outfit', 
                fontWeight: '500', 
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Updating...' : 'Update Student'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStudent;